import React, { useState } from 'react';
import { auth } from '../firebase';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import TopNavigation from '../components/TopNavigation';
import type { AdPlacement } from '../utils/ads';
import { createAd } from '../utils/secureFunctions';

const allPlacements: AdPlacement[] = ['home_feed', 'sidebar', 'midroll_pulse', 'midroll_testimony'];

const AdsManager: React.FC = () => {
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [ctaText, setCtaText] = useState('Learn more');
  const [ctaUrl, setCtaUrl] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [placements, setPlacements] = useState<AdPlacement[]>(['home_feed']);
  const [startAt, setStartAt] = useState<string>('');
  const [endAt, setEndAt] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const togglePlacement = (p: AdPlacement) => {
    setPlacements(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const handleCreate = async () => {
    if (!auth.currentUser) return alert('Please log in');
    if (!mediaFile) return alert('Please select an image or video');

    setSubmitting(true);
    try {
      setUploadProgress(0);
      const upload = await uploadModeratedMedia({
        folder: 'ads',
        field: 'mediaURL',
        file: mediaFile,
        contentType: mediaFile.type || (mediaFile.type.startsWith('video') ? 'video/mp4' : 'image/jpeg'),
        onProgress: (pct) => setUploadProgress(pct),
      });
      const mediaURL = upload.objectKey;
      const moderationTokens = { mediaURL: upload.proof };

      await createAd({
        title: headline || 'Ad',
        description: description || '',
        imageURL: mediaFile.type.startsWith('video') ? undefined : mediaURL,
        videoURL: mediaFile.type.startsWith('video') ? mediaURL : undefined,
        actionType: 'url',
        actionValue: ctaUrl,
        buttonText: ctaText,
        placements: placements as unknown as string[],
        budgetUsd: 0,
        reviewStatus: 'needs_review',
        aiScan: { result: 'unknown', mode: 'client' },
        moderationTokens,
      });

      setHeadline(''); setDescription(''); setCtaText('Learn more'); setCtaUrl(''); setMediaFile(null); setPlacements(['home_feed']);
      alert('Ad created');
    } catch (e) {
      console.error(e);
      alert('Failed to create ad');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const label: React.CSSProperties = { fontSize: 13, color: '#5B6E6E', marginBottom: 6 };
  const input: React.CSSProperties = { padding: '10px 12px', borderRadius: 8, border: '1px solid #DDD', fontSize: 14, boxSizing: 'border-box', maxWidth: '100%' };

  return (
    <div style={{ background: '#F5EDE1', minHeight: '100vh' }}>
  <TopNavigation />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 'clamp(12px, 4vw, 20px)', boxSizing: 'border-box' }}>
        <h2 style={{ marginTop: 16 }}>Create an Ad</h2>
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 16 }}>
            <div>
              <div style={label}>Headline</div>
              <input style={{ ...input, width: '100%' }} value={headline} onChange={e => setHeadline(e.target.value)} placeholder="E.g., Grow your business with Belivah" />
            </div>
            <div>
              <div style={label}>CTA Text</div>
              <input style={{ ...input, width: '100%' }} value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="Shop now" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={label}>Description</div>
              <textarea style={{ ...input, width: '100%', minHeight: 80, resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Tell people about your product..." />
            </div>
            <div>
              <div style={label}>CTA URL</div>
              <input style={{ ...input, width: '100%' }} value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="https://example.com" />
            </div>
            <div>
              <div style={label}>Media (image/video)</div>
              <input type="file" accept="image/*,video/*" style={{ maxWidth: '100%' }} onChange={e => setMediaFile(e.target.files?.[0] || null)} />
              {uploadProgress > 0 && <div style={{ fontSize: 12, color: '#5B6E6E' }}>Uploading: {Math.round(uploadProgress)}%</div>}
            </div>
            <div>
              <div style={label}>Start</div>
              <input type="datetime-local" style={{ ...input, width: '100%' }} value={startAt} onChange={e => setStartAt(e.target.value)} />
            </div>
            <div>
              <div style={label}>End</div>
              <input type="datetime-local" style={{ ...input, width: '100%' }} value={endAt} onChange={e => setEndAt(e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={label}>Placements</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {allPlacements.map(p => (
                <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F5EDE1', padding: '8px 12px', borderRadius: 8 }}>
                  <input type="checkbox" checked={placements.includes(p)} onChange={() => togglePlacement(p)} />
                  {p}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button disabled={submitting} onClick={handleCreate} style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#17A2A9', color: '#fff', cursor: 'pointer' }}>
              {submitting ? 'Creating…' : 'Create Ad'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdsManager;
